/**
 * BottomDock — global kid-app navigation, upgraded through v0.15.3.
 *
 * Phone/portrait renders as a fixed bottom dock. iPad landscape renders as a
 * left rail, and v0.15.3 moves the account/wallet/settings status into that
 * rail so the main Park scene no longer has a detached top system row.
 *
 * 调用关系：
 *   - AppShellV2 renders this once for all authenticated child routes.
 *   - Uses AppContext for child identity, active pet, and wallet balance.
 *   - Links route through react-router-dom only; no backend calls here.
 *
 * 关键约束：
 *   - Keep this component render-only. Bond fetching remains in TopBar.
 *   - Do not add nav destinations here without updating redirects in App.tsx.
 *   - The rail status is CSS-hidden on phone so bottom dock height stays stable.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useLang, t } from '../../i18n';
import type { AppContext } from '../../App';
import { Coins } from '../ui/Coins';

interface DockItem {
  /** Route target. Old aliases redirect in App.tsx, so keep these canonical. */
  to: string;
  /** Exact-match flag for the root Park route. */
  end?: boolean;
  /** Visual symbol shown above/next to the label. */
  icon: string;
  /** Chinese label shown when the current language is zh. */
  zh: string;
  /** English label shown when the current language is en. */
  en: string;
  /** Hide until the kid has hatched their first pet. */
  needsPet?: boolean;
  /** Primary items stay on the iPad-landscape left rail; secondary fold into "More". */
  tier: 'primary' | 'secondary';
}

// v0.14.0 — collapsed from 7 entries down to 4. PR-2 had 5 (with
// separate /park); PR-2.5 merges Park's 4 sub-tabs into "/" alongside
// the main 3D scene, so kids see one entry covering everything
// "playground-shaped" (主场景 + 动物园 + 宠物店 + 游戏 + 宠物).
const DOCK_ITEMS: DockItem[] = [
  { to: '/',         end: true, icon: '🏞️', zh: '乐园', en: 'Park',     tier: 'primary' },
  { to: '/schedule', icon: '📅', zh: '日程', en: 'Schedule', needsPet: true, tier: 'primary' },
  { to: '/learn',    icon: '📚', zh: '学习', en: 'Learn',    needsPet: true, tier: 'primary' },
  { to: '/me',       icon: '👤', zh: '我',   en: 'Me',       tier: 'primary' },
];

interface BottomDockProps {
  /** App-wide child context loaded from /me; render-only in this component. */
  ctx: AppContext;
}

export function BottomDock({ ctx }: BottomDockProps) {
  const lang = useLang();
  const hasPet = !!ctx.activePet;
  const items = DOCK_ITEMS.filter(d => hasPet || !d.needsPet);
  const secondary = items.filter(d => d.tier === 'secondary');
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!moreOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!moreRef.current) return;
      if (moreRef.current.contains(e.target as Node)) return;
      setMoreOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [moreOpen]);

  return (
    <nav className="xpb-bottom-dock" aria-label={lang === 'zh' ? '主导航' : 'Main nav'}>
      <div className="xpb-dock-rail-status" aria-label={ctx.child.display_name || ctx.child.name}>
        <Link to="/me/profile" className="xpb-dock-rail-profile">
          <span className="xpb-dock-rail-avatar" aria-hidden>{ctx.child.avatar || '🐾'}</span>
          <span className="xpb-dock-rail-copy">
            <b>{ctx.child.display_name || ctx.child.name}</b>
            {ctx.activePet && <small>{ctx.activePet.nickname}</small>}
          </span>
        </Link>
        <div className="xpb-dock-rail-actions">
          <Link
            to="/me/wallet"
            className="xpb-dock-rail-wallet"
            aria-label={t('topbar.aria.wallet')}
            data-coin-target="true"
          >
            <Coins sen={Number(ctx.balance) || 0} size="sm" />
          </Link>
          <Link
            to="/me"
            className="xpb-dock-rail-settings"
            aria-label={t('topbar.aria.settings')}
          >
            ⚙
          </Link>
        </div>
      </div>
      {items.map(d => (
        <NavLink
          key={d.to}
          to={d.to}
          end={d.end}
          data-tier={d.tier}
          className={({ isActive }) => `xpb-dock-link ${isActive ? 'active' : ''}`.trim()}
        >
          <span className="xpb-dock-icon" aria-hidden>{d.icon}</span>
          <span className="xpb-dock-label">
            {lang === 'zh' ? d.zh : d.en}
          </span>
        </NavLink>
      ))}
      {/* "More" pill — only visible on iPad-landscape rail via CSS. */}
      {secondary.length > 0 && (
        <div className="xpb-dock-more-wrap" ref={moreRef}>
          <button
            type="button"
            className={`xpb-dock-link xpb-dock-more ${moreOpen ? 'open' : ''}`}
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(v => !v)}
          >
            <span className="xpb-dock-icon" aria-hidden>⋯</span>
            <span className="xpb-dock-label">{t('nav.more.label')}</span>
          </button>
          {moreOpen && (
            <div className="xpb-dock-more-sheet" role="menu">
              {secondary.map(d => (
                <NavLink
                  key={d.to}
                  to={d.to}
                  end={d.end}
                  role="menuitem"
                  className={({ isActive }) => `xpb-dock-more-item ${isActive ? 'active' : ''}`.trim()}
                  onClick={() => setMoreOpen(false)}
                >
                  <span aria-hidden>{d.icon}</span>
                  <span>{lang === 'zh' ? d.zh : d.en}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
